<template>
  <div v-if="localVisible" class="br-name-change-modal">
    <div class="br-name-change-modal__scrim" @click="close"></div>
    <div
      class="br-name-change-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="br-name-change-title"
    >
      <div class="br-name-change-modal__header">
        <h2 id="br-name-change-title" class="br-name-change-modal__title">
          {{ $t('lbl_EMPBE_BR_RequestNameChange') }}
        </h2>
        <p class="br-name-change-modal__desc">{{ $t('lbl_EMPBE_BR_NameChange_Modal_Desc') }}</p>
      </div>

      <div class="br-name-change-modal__section">
        <p class="br-name-change-modal__label">
          {{ $t('lbl_EMPBE_BR_NameChange_UpdateDisplayName') }}
        </p>

        <label class="br-name-change-modal__outlined">
          <span class="br-name-change-modal__outlined-label-row">
            <span class="br-name-change-modal__outlined-label">
              {{ $t('lbl_EMPBE_BR_NameChange_DisplayName_Zh') }}
            </span>
          </span>
          <input
            v-model="displayNameZh"
            type="text"
            class="br-name-change-modal__outlined-input"
            :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_Zh_Placeholder')"
          />
        </label>

        <label class="br-name-change-modal__outlined">
          <span class="br-name-change-modal__outlined-label-row">
            <span class="br-name-change-modal__outlined-label">
              {{ $t('lbl_EMPBE_BR_NameChange_DisplayName_En') }}
            </span>
          </span>
          <input
            v-model="displayNameEn"
            type="text"
            class="br-name-change-modal__outlined-input"
            :placeholder="$t('lbl_EMPBE_BR_Confirm_DisplayName_En_Placeholder')"
          />
        </label>
      </div>

      <p v-if="errorMessage" class="br-name-change-modal__error">{{ errorMessage }}</p>

      <div class="br-name-change-modal__actions">
        <button type="button" class="br-name-change-modal__cancel" @click="close">
          {{ $t('lbl_EMPBE_BR_NameChange_Cancel') }}
        </button>
        <button
          type="button"
          class="br-name-change-modal__submit"
          :disabled="submitting"
          @click="submit"
        >
          {{ $t('lbl_EMPBE_BR_NameChange_Request') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BusinessRegistrationService } from '../../../_services/BusinessRegistration.Service';
import { resolveApiErrorMessage } from '../../../_helpers/apiResponse';
import { getBrSubmissionApprovalId } from '../utils/brDocument';
import { getApprovedBrForNameChangeOptions } from '../utils/brNameChange';

export default {
  name: 'BrNameChangeModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    documentList: {
      type: Array,
      default: () => [],
    },
    /** 来自当前生效卡铅笔 / Set as Current：作为更名 sourceApprovalId */
    sourceApprovalId: {
      type: [Number, String],
      default: null,
    },
    currentDisplayNameEn: {
      type: String,
      default: '',
    },
    currentDisplayNameLocal: {
      type: String,
      default: '',
    },
    empCompanyId: {
      type: [Number, String],
      default: null,
    },
    accountId: {
      type: [Number, String],
      default: null,
    },
    submitType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localVisible: false,
      resolvedSourceApprovalId: null,
      displayNameEn: '',
      displayNameZh: '',
      submitting: false,
      errorMessage: '',
    };
  },
  computed: {
    approvedBrItems() {
      return getApprovedBrForNameChangeOptions(this.documentList);
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        this.localVisible = value;
        if (value) {
          this.resetForm();
        }
      },
    },
    localVisible(value) {
      if (!value && this.visible) {
        this.$emit('close');
      }
    },
  },
  methods: {
    resetForm() {
      this.errorMessage = '';
      this.displayNameEn = this.currentDisplayNameEn || '';
      this.displayNameZh = this.currentDisplayNameLocal || '';
      this.resolvedSourceApprovalId = this.resolveSourceApprovalId();
    },
    resolveSourceApprovalId() {
      const fromProp = Number(this.sourceApprovalId);
      if (Number.isFinite(fromProp) && fromProp > 0) {
        const matched = this.approvedBrItems.some(
          (item) => Number(getBrSubmissionApprovalId(item)) === fromProp,
        );
        if (matched) {
          return fromProp;
        }
      }
      const first = this.approvedBrItems[0];
      const id = Number(getBrSubmissionApprovalId(first));
      return Number.isFinite(id) && id > 0 ? id : null;
    },
    close() {
      this.localVisible = false;
    },
    validate() {
      if (!this.resolvedSourceApprovalId) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_SelectBr');
        return false;
      }
      const en = (this.displayNameEn || '').trim();
      const zh = (this.displayNameZh || '').trim();
      if (!en && !zh) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_Required');
        return false;
      }
      const currentEn = (this.currentDisplayNameEn || '').trim();
      const currentZh = (this.currentDisplayNameLocal || '').trim();
      if (en === currentEn && zh === currentZh) {
        this.errorMessage = this.$t('lbl_EMPBE_BR_NameChange_Validation_NoChange');
        return false;
      }
      this.errorMessage = '';
      return true;
    },
    async submit() {
      if (!this.validate()) {
        return;
      }

      const payload = {
        sourceApprovalId: Number(this.resolvedSourceApprovalId),
        displayNameEn: (this.displayNameEn || '').trim(),
        displayNameZh: (this.displayNameZh || '').trim(),
      };
      const empCompanyId = Number(this.empCompanyId);
      if (Number.isFinite(empCompanyId) && empCompanyId > 0) {
        payload.empCompanyId = empCompanyId;
      }
      const accountId = Number(this.accountId);
      if (Number.isFinite(accountId) && accountId > 0) {
        payload.accountId = accountId;
      }
      const submitType = String(this.submitType || '').trim();
      if (submitType) {
        payload.submitType = submitType;
      }

      this.submitting = true;
      try {
        const res = await BusinessRegistrationService.RequestDisplayNameChange(payload);

        if (!res?.isSuccessful) {
          this.errorMessage = resolveApiErrorMessage(
            res,
            this.$t('lbl_EMPBE_BR_NameChange_Failed'),
          );
          return;
        }

        this.$emit('submitted', res.data);
        this.localVisible = false;
      } catch (e) {
        this.errorMessage = resolveApiErrorMessage(
          { systemErrorMsg: [e?.message] },
          this.$t('lbl_EMPBE_BR_NameChange_Failed'),
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
